// App.js
import React, { useEffect, useRef, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { UserProvider, useUser } from "./comp/UserContext/UserContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import ToastContainer from "./comp/SubComp/ToastContainer";
import ProtectedRoute from "./comp/ProtectedRoute";
import AdminProtectedRoute from "./comp/AdminProtectedRoute";
import CookieBanner from "./consent/CookieBanner";
import WelcomeModal from "./comp/WelcomeModal";
import { hasConsentedTo, applyConsent } from "./consent/cookieConsent";
// axios instance that already attaches Bearer token when available
import api from "./comp/Services/authService";
import "./css/Container.css";

// Lazy load page components for better performance
const Profile = lazy(() => import("./pages/ProfilePage"));
const ProfileEdit = lazy(() => import("./pages/ProfileEdit"));
const About = lazy(() => import("./pages/About"));
const Forum = lazy(() => import("./pages/Forum"));
const ForumPost = lazy(() => import("./pages/ForumPost"));
const Help = lazy(() => import("./pages/Help"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
// eslint-disable-next-line no-unused-vars
const Cookies = lazy(() => import("./pages/Cookies"));
const ForgotPassword = lazy(() => import("./comp/PasswordComp/ForgotPassword"));
const VerifyCode = lazy(() => import("./comp/PasswordComp/VerifyCode"));
const ResetPassword = lazy(() => import("./comp/PasswordComp/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LoginAdmin = lazy(() => import("./pages/LoginAdmin"));
const PixelGrid = lazy(() => import("./pages/PixelGrid"));
const StripeCheckoutSuccess = lazy(() => import("./pages/StripeCheckoutSuccess"));
const StripeCheckoutCancel = lazy(() => import("./pages/StripeCheckoutCancel"));
const ShareProfileCard = lazy(() => import("./comp/ShareProfileCard"));
const Announcements = lazy(() => import("./pages/Announcements"));

/* First-party traffic analytics, modelled on Google Analytics:
 * - visitor id: persistent per browser, counted as one "user"
 * - session id: shared by all tabs, rotates after 30 min without activity
 * - engagement: only time the tab is visible counts, flushed with each ping
 * The ids are only saved on the device with analytics consent. Without it they
 * live in memory for this page load, so visits are still counted but nothing
 * is stored in the browser (each full page load then counts as a new visitor).
 */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const PING_INTERVAL_MS = 60 * 1000;

function randomId() {
  const random = Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${random}${Date.now().toString(36)}`;
}

const memoryIds = {};
const idStore = {
  get(key) {
    if (!hasConsentedTo("analytics")) return memoryIds[key] ?? null;
    try {
      // Carry over ids created before the visitor opted in
      if (memoryIds[key] && !localStorage.getItem(key)) localStorage.setItem(key, memoryIds[key]);
      return localStorage.getItem(key);
    } catch (_) {
      return memoryIds[key] ?? null;
    }
  },
  set(key, value) {
    memoryIds[key] = value;
    if (!hasConsentedTo("analytics")) return;
    try {
      localStorage.setItem(key, value);
    } catch (_) {}
  },
};

// Kept under its original key so browsers seen before stay "returning" visitors
function getVisitorId() {
  let vid = idStore.get("site_session_id");
  if (!vid) {
    vid = randomId();
    idStore.set("site_session_id", vid);
  }
  return vid;
}

// Returns the current session id, starting a new session after inactivity
function touchSession() {
  const now = Date.now();
  let session = null;
  try {
    session = JSON.parse(idStore.get("site_analytics_session"));
  } catch (_) {}
  const isNew = !session?.id || now - (session.lastActivity || 0) > SESSION_TIMEOUT_MS;
  if (isNew) session = { id: randomId() };
  session.lastActivity = now;
  idStore.set("site_analytics_session", JSON.stringify(session));
  return { sessionId: session.id, isNew };
}

// `withSource` sends referrer/UTM even mid-session (e.g. on a full page load);
// the server only keeps them for the page that starts a session
function sendPageView(path, withSource = false) {
  const { sessionId, isNew } = touchSession();
  const includeSource = isNew || withSource;
  const params = new URLSearchParams(window.location.search);
  const payload = {
    visitorId: getVisitorId(),
    sessionId,
    path,
    referrer: includeSource ? document.referrer || null : null,
    utmSource: includeSource ? params.get("utm_source") : null,
  };
  api.post("/user_data_endpoint/track/pageview", payload).catch(() => {});
}

/** Heartbeat while the tab is visible: keeps "active now" accurate and
 * reports visible time since the last ping as engagement time */
function useSiteHeartbeat() {
  useEffect(() => {
    let visibleSince = document.visibilityState === "visible" ? Date.now() : null;
    let engagedMs = 0;

    const takeEngagedSeconds = () => {
      if (visibleSince) {
        engagedMs += Date.now() - visibleSince;
        visibleSince = Date.now();
      }
      const seconds = Math.round(engagedMs / 1000);
      engagedMs -= seconds * 1000;
      return seconds;
    };

    const ping = () => {
      const { sessionId, isNew } = touchSession();
      if (isNew) {
        // Came back after the session expired: that's a new visit
        engagedMs = 0;
        sendPageView(window.location.pathname);
        return;
      }
      api
        .post("/user_data_endpoint/track/ping", {
          visitorId: getVisitorId(),
          sessionId,
          engagedSeconds: takeEngagedSeconds(),
        })
        .catch(() => {});
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") ping();
    }, PING_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        ping(); // flush visible time before the tab goes to the background
        visibleSince = null;
      } else {
        visibleSince = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}

/** Track page views on actual route changes (navigation) */
function usePageViewTracking() {
  const location = useLocation();
  const isFirstView = useRef(true);

  useEffect(() => {
    // Only a full page load can carry an external referrer; route changes are internal
    sendPageView(location.pathname, isFirstView.current);
    isFirstView.current = false;

    // Google Analytics only runs with analytics consent
    if (hasConsentedTo("analytics") && typeof window.gtag === "function") {
      window.gtag('config', 'G-HHQGPXVELY', {
        page_path: location.pathname + location.search,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]); // Track on every route change
}

function App() {
  useSiteHeartbeat();

  // Load or stop Google Analytics to match the stored choice. Later changes
  // are applied by setConsent() itself.
  useEffect(() => {
    applyConsent();
  }, []);

  return (
    <UserProvider>
      <NotificationProvider>
        <BrowserRouter>
          <AppRoutes />
          {/* Render once so it's visible site-wide */}
          <CookieBanner />
          <WelcomeModal />
          <ToastContainer />
        </BrowserRouter>
      </NotificationProvider>
    </UserProvider>
  );
}

const HomeRedirectToRegister = () => {
  const navigate = useNavigate();
  const { category, pixel } = useParams();

  useEffect(() => {
    navigate("/Home", { state: { category, pixel, showRegister: true } });
  }, [navigate, category, pixel]);

  return null;
};

function AppRoutes() {
  const { user, loading } = useUser();
  usePageViewTracking(); // Track page views on route changes

  if (loading) {
    return <div>Loading...</div>;
  }

  // Loading fallback for lazy-loaded pages
  const PageLoader = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      color: 'var(--profile-text, #fff)',
      fontSize: '1rem'
    }}>
      Loading...
    </div>
  );

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          index
          element={
            user ? (
              user.isAdmin ? (
                <Navigate to="/admindashboard" replace />
              ) : (
                <Profile />
              )
            ) : (
              <PixelGrid />
            )
          }
        />

      <Route path="/Home" element={<PixelGrid />} />
      <Route
        path="/membership"
        element={
          <ProtectedRoute>
            <ProfileEdit initialMenu="membershipPayments" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ProfilePage"
        element={<Profile />}
      />
      <Route
        path="/ProfilePage/:username"
        element={<Profile />}
      />
      <Route
        path="/ProfileEdit"
        element={
          <ProtectedRoute>
            <ProfileEdit />
          </ProtectedRoute>
        }
      />

      <Route path="/share/:category/:pixelNumber" element={<ShareProfileCard />} />

      <Route path="/news" element={<Announcements />} />
      <Route path="/About" element={<About />} />
      <Route path="/Forum" element={<Forum />} />
      <Route path="/forum/post/:id" element={<ForumPost />} />
      <Route path="/Help" element={<Help />} />
      <Route path="/Privacy" element={<Privacy />} />
      <Route path="/Terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />

      {/* Admin */}
      <Route path="/admin" element={<LoginAdmin />} />
      <Route
        path="/admindashboard"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />

      {/* Redirect shared register links to Home with pixel state */}
      <Route
        path="/register/:category/:pixel"
        element={<HomeRedirectToRegister />}
      />

      {/* Password Reset */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-code" element={<VerifyCode />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/stripe/checkout-success" element={<StripeCheckoutSuccess />} />
      <Route path="/stripe/checkout-cancel" element={<StripeCheckoutCancel />} />

      {/* Share profile by category and pixel number - placed after all specific routes */}
      <Route path="/:category/:pixelNumber" element={<ShareProfileCard />} />

      {/* Share profile by username - must be last before catch-all */}
      <Route path="/:username" element={<ShareProfileCard />} />

      {/* Default */}
      <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
}

export default App;
